import { Account } from "@radixdlt/radix-dapp-toolkit";
import { type LoaderFunctionArgs, type MetaFunction } from "react-router";
import { useFetcher, useLoaderData, useRevalidator } from "react-router";
import dayjs from "dayjs";
import { useState } from "react";
import AccountSelector from "~/components/account-selector";
import TokenRow from "~/components/token-row";
import TransactionButton from "~/components/transaction-button";
import { trackEvent } from "~/lib/analytics";
import { fetchValidatorAPY, validatorXRDAPY } from "~/lib/apy";
import { cache } from "~/lib/cache";
import { generateClaimManifest } from "~/lib/manifests";
import { loader as walletLoader } from "~/routes/rewards.$account";
import { useRadix } from "~/rdt/radixProvider";
import { getPair } from "~/lib/models/pair";

export const meta: MetaFunction = () => {
	return [
		{
			title: "Claim your ASTRL rewards",
			description: "Receive ASTRL rewards for contributing to the Astrolescent ecosystem, like staking XRD on our validator node, or adding liquidity to DefiPlaza.",
		},
	];
};

export async function loader({ request, context }: LoaderFunctionArgs) {
	const apys = await cache("https://defiplaza.net/cache/apys", 3600, async () => {
		const validatorAPY = await fetchValidatorAPY(context, "validator_rdx1s08nr8ukas7yklpf7q0dg9s3resvmhjau9r5ye3vvkrhpycayjrfrw", 7500 * 52);
		// const defiPlazaAPY = await fetchLiquidityAPY(context, "pool_rdx1c47jlmd9stptfy2a7e39wnjfechu72q9ggus29x0mqf98m8xt70rx2", 5000 * 52);
		const xrdAPY = await validatorXRDAPY(context, "validator_rdx1s08nr8ukas7yklpf7q0dg9s3resvmhjau9r5ye3vvkrhpycayjrfrw");

		const [pairRow] = await getPair("component_rdx1cppd8rq7gfwad75z56mz9tldqmw4aps48hqnx2stf4eeew8v6tyd72");
		const defiPlazaAPY = pairRow.Pair.quoteAPY;

		// Staking APY
		// fetch staking ratio history
		// const historyResponse = await context.cloudflare.env.API_BINDING.fetch(`${context.cloudflare.env.API_URL}/staking`);
		// let history = await historyResponse.json();
		// history.reverse();

		return {
			"1": validatorAPY,
			"2": defiPlazaAPY,
			"3": 0, //calculateStakingAPY(history),
			"4": xrdAPY
		};
	});

	return ({
		apys,
	});
}

export default function ClaimPage() {
	const { apys } = useLoaderData<typeof loader>();
	const walletFetcher = useFetcher<typeof walletLoader>();
	const [account, setAccount] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);


	const { sendTransaction } = useRadix();
	const revalidator = useRevalidator();

	function onAccount(account: Account) {
		setAccount(account.address);

		walletFetcher.load(`/rewards/${account.address}`);
		setError("");

		console.log(account)
	}

	async function submitTransaction(event) {
		event.preventDefault();
		setError("");

		if (!account || !walletFetcher.data || !walletFetcher.data.claims.length || !walletFetcher.data.localId) {
			return;
		}

		let total = walletFetcher.data.claims.reduce((total, e) => total + (+e.remaining || 0), 0);

		if (total == 0) {
			setError("You don't have any ready rewards to claim.");
			return;
		}

		trackEvent("send_tx", {
			type: "claim_rewards",
		});

		const manifest = generateClaimManifest(account, walletFetcher.data.localId, walletFetcher.data.claims);
		const txResult = await sendTransaction(manifest, "Thank you for contributing 🫡");

		revalidator.revalidate();
	}

	async function submitStake(event) {

	}

	return (
		<>
			<div className="spacy-y-4 mx-4 mb-8 xl:mx-auto xl:w-3/5">
				<div className="py-4 lg:p-8 ">
					<h1 className="text-3xl font-bold lg:text-4xl">DefiPlaza Validator Rewards</h1>

					<p className="mt-2">When you <a href="https://dashboard.radixdlt.com/network-staking/validator_rdx1s08nr8ukas7yklpf7q0dg9s3resvmhjau9r5ye3vvkrhpycayjrfrw" target="_blank" className="underline">stake XRD with the DefiPlaza Validator</a>, you earn <strong>{apys['4']}% APY</strong>, paid out weekly in DFP2 LP tokens, bought on the open market from the staking fees the node generates. </p>
					
					<p className="mt-2">DFP2 is the index token of the Radix ecosystem, backed by liquidity across major Radix projects and used in DefiPlaza pools, giving you diversified exposure, while the LP tokens generate an additional <strong>{Math.round(apys['2'] * 1000) / 10}% APY</strong> that compounds your rewards over time.</p>
				</div>

				<section className="flex flex-col space-y-4">
					<AccountSelector onAccount={onAccount}></AccountSelector>

					{walletFetcher.data?.error || error ? <p className="text-md bg-red-800 px-4 py-2 text-center font-medium text-white">{walletFetcher.data?.error || error}</p> : null}

					<div className="dark:bg-background-900 rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8 space-y-6">

						<div className="">
							<div className="items-top flex justify-between ">
								<label htmlFor="inputAmount" className="text-primary-800 dark:text-primary-100 truncate font-medium lg:text-sm">
									Total rewards
								</label>
							</div>
							<div className="justify-left flex flex-wrap items-center">
								<TokenRow
									token={{
										symbol: "DFP2LP",
										iconUrl: "/assets/img/babylon/defiplaza-icon.png",
									}}
								/>

								<p className="font-optical-24 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl lining-nums placeholder-neutral-400 focus:border-transparent focus:ring-astro focus:outline-hidden">
									{(walletFetcher.data?.claims.reduce((total, e) => total + (+e.remaining || 0), 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })}
								</p>
							</div>
						</div>

						{walletFetcher.data?.since ? <p className="text-center text-xs text-gray-400">Rewards added up until {dayjs(walletFetcher.data?.since * 1000).toLocaleString()}</p> : null}

					</div>

					<TransactionButton title="Claim rewards" onClick={submitTransaction} />

				</section>


				{/* <table className="mt-6 w-full border-spacing-8 overflow-hidden rounded-lg text-sm text-white shadow-lg">
				<thead className="tracking-astro px-4 py-2 text-left text-xs text-white uppercase">
					<tr>
						<th className="font-optical-24-550 sticky top-0 bg-black/30 px-3 py-3 backdrop-blur-sm">Incentive</th>
						<th className="font-optical-24-550 sticky top-0 bg-black/30 px-3 py-3 text-right backdrop-blur-sm">APY</th>
						<th className="font-optical-24-550 sticky top-0 bg-black/30 px-3 py-3 text-right backdrop-blur-sm">Your rewards</th>
					</tr>
				</thead>
				<tbody className="_backdrop-blur-sm space-y-3 bg-white/30 text-sm">
					<tr>
						<td className="font-optical-14-550 tracking-astro px-3 py-3">
							<a href="https://dashboard.radixdlt.com/network-staking/validator_rdx1sw54cuswwzlcgw2zh3ax93pddnsm78qwwhmtvz650q84yyckzkh7nk" target="_blank">
								Validator Staking
							</a>
						</td>
						<td className="px-3 py-3 text-right font-mono">{apys["1"]}% + {apys["4"]}% XRD</td>
						<td className="px-3 py-3 text-right font-mono">{Math.floor(+(walletFetcher.data?.claims.find((c) => c?.type == 1)?.remaining || 0))} ASTRL</td>
					</tr>
				</tbody>
			</table> */}
			</div>
		</>
	);
}
